import { Row, Col, Card, Table, Button, Input, Tooltip, Tag } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { getSortOrder } from "../../constants/conts";
import { EyeIcon } from "lucide-react";
const Search = Input.Search;

const STATUS_COLORS = {
  created: "gold",
  paid: "green",
  failed: "red",
  refunded: "default",
};

function Index() {

  const sectionName = "Transaction";
  const routeName = "transaction";

  const api = {
    list: apiPath.listTransaction,
  };

  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sortBy, setSortBy] = useState('');
  const lastFiltersRef = useRef({});
  const debouncedSearchText = useDebounce(searchText, 300);

  const columns = [
    {
      title: "Order ID",
      dataIndex: "razorpay_order_id",
      key: "razorpay_order_id",
      width: 240,
      render: (_, { razorpay_order_id }) => (
        <span style={{ whiteSpace: "nowrap" }}>{razorpay_order_id || "-"}</span>
      ),
    },
    {
      title: "User",
      key: "user_name",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "user_name"),
      render: (_, { user_name, user_phone }) => (
        <div>
          <span className="cap">{user_name || '-'}</span>
          {user_phone ? <div style={{ fontSize: 12, color: '#888' }}>{user_phone}</div> : null}
        </div>
      ),
    },
    {
      title: "Plan",
      dataIndex: "plan_name",
      key: "plan_name",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "plan_name"),
      render: (_, { plan_name, plan_type }) => (
        <div>
          <span className="cap">{plan_name || '-'}</span>
          {plan_type ? <div style={{ fontSize: 12, color: '#888' }} className="cap">{plan_type}</div> : null}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "amount"),
      render: (_, { amount }) => <span>&#8377;{amount ? amount.toLocaleString('en-IN') : 0}</span>,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (_, { duration }) => duration ? `${duration} Month${duration > 1 ? 's' : ''}` : '-',
    },
    {
      title: "Status",
      key: "status",
      filters: [
        { text: 'Created', value: 'created' },
        { text: 'Paid', value: 'paid' },
        { text: 'Failed', value: 'failed' },
        { text: 'Refunded', value: 'refunded' },
      ],
      render: (_, { status }) => (
        <Tag color={STATUS_COLORS[status] || 'default'} className="cap">{status || '-'}</Tag>
      ),
    },
    {
      title: "Date",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => moment(created_at).format('DD-MMM-YYYY HH:mm'),
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title={"View " + sectionName} color={"purple"} key={"view" + routeName}>
          <Button onClick={() => navigate(`/${routeName}/view/${record._id}`)}>
            <EyeIcon size={15} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, lastFiltersRef.current, sortBy);
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize]);

  const fetchData = (pagination, filters, sortValue = '') => {
    const filterStatus = filters ? filters.status : null;

    request({
      url:
        api.list +
        `?status=${filterStatus ? filterStatus.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChange = (pag, filters, sorter) => {
    const { order, field } = sorter || {};

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    lastFiltersRef.current = filters || {};

    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            variant={false}
            className="criclebox tablespace mb-24"
            title={sectionName + " Management"}
            extra={
              <Search
                size="large"
                onChange={onSearch}
                value={searchText}
                onPressEnter={onSearch}
                placeholder="Search by user, plan or order id"
              />
            }
          >
            <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
            <div className="table-responsive customPagination">
              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  responsive: true,
                  defaultPageSize: 20,
                  pageSizeOptions: ['10', '20', '30', '50', '100']
                }}
                onChange={handleChange}
                className="ant-border-space"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Index;
